<template>
	<v-app
		style="margin-top: -20px; margin-bottom: -20px;"
		sticky-container
		fluid
	>
		<a href="#main-content" class="co-skip-link">Skip to main content</a>
		<nav aria-label="Page shortcuts" class="co-shortcut-nav sr-only">
			<ul>
				<li><router-link to="/">Home</router-link></li>
				<li><router-link to="/reserve">Reserve a room</router-link></li>
				<li><router-link to="/attend">Events</router-link></li>
				<li><a href="#main-content">Skip to main content</a></li>
			</ul>
		</nav>
		<v-row>
			<co-header v-if="!embedded"></co-header>
		</v-row>
		<v-content
			id="main-content"
			tabindex="-1"
			:aria-label="routeHeading"
			class="content mx-auto"
		>
			<h1 class="sr-only">{{ routeHeading }}</h1>
			<router-view />
		</v-content>
		<v-row>
			<co-footer v-if="!embedded"></co-footer>
		</v-row>
	</v-app>
</template>
<script>
import CoFooter from './components/footer.vue';
import CoHeader from './components/header.vue';
import { scheduleHeightPost, setupEmbedBridge } from './utils/embed-bridge';

export default {
	created() {
		this.$store.dispatch('fetchClientDetails');
	},
	updated() {
		if (this.embedded) {
			scheduleHeightPost();
		}
	},
	watch: {
		embedded: {
			immediate: true,
			handler(value) {
				if (!value) {
					return;
				}
				this.$nextTick(() => {
					setupEmbedBridge();
					scheduleHeightPost();
				});
			}
		},
		$route() {
			if (this.embedded) {
				this.$nextTick(() => {
					scheduleHeightPost();
				});
			}
		}
	},
	computed: {
		embedded() {
			const value = String(
				this.$route.query.embedded || ''
			).toLowerCase();
			return value === 'true' || value === '1';
		},
		routeHeading() {
			return (this.$route.meta && this.$route.meta.title) || 'Communico';
		}
	},
	components: {
		CoFooter,
		CoHeader
	}
};
</script>
<style lang="scss">
.v-application--wrap {
	min-height: 0 !important;
}

.content {
	max-width: 988px !important;
	width: 100%;
}

// Visually-hidden utility for headings and labels that are meaningful to
// assistive tech but should not affect the visual layout.
.sr-only {
	position: absolute !important;
	width: 1px !important;
	height: 1px !important;
	padding: 0 !important;
	margin: -1px !important;
	overflow: hidden !important;
	clip: rect(0, 0, 0, 0) !important;
	white-space: nowrap !important;
	border: 0 !important;
}

// Skip-to-main-content link. Hidden until keyboard focus moves to it.
// `!important` on color is required to win against Vuetify's default
// `.v-application a { color: var(--v-anchor-base) }` rule, which
// otherwise repaints the link in the partner theme's anchor colour and
// breaks contrast against the dark background.
.co-skip-link {
	position: absolute;
	top: -40px;
	left: 8px;
	z-index: 10000;
	padding: 10px 16px;
	background: #111111 !important;
	color: #ffffff !important;
	border-radius: 4px;
	text-decoration: none;
	font-weight: 600;
	transition: top 0.15s ease;

	&:focus {
		top: 8px;
		outline: 3px solid #19b4c7;
		outline-offset: 2px;
	}
}

// Ensure the main landmark isn't given a default focus outline when it
// receives programmatic focus from the skip link.
#main-content:focus {
	outline: none;
}

// Force a perceivable focus indicator for keyboard users across Vuetify
// buttons, links, and inputs. Vuetify 2 relies on :focus-visible, which
// some default themes dull out below WCAG 2.4.7 threshold.
a:focus-visible,
button:focus-visible,
.v-btn:focus-visible,
.v-input input:focus-visible,
.v-input .v-input__slot:focus-within {
	outline: 2px solid #19b4c7 !important;
	outline-offset: 2px !important;
}

// Reflow guard: Vuetify's v-row applies negative horizontal margins to
// offset child padding. On very narrow viewports this creates a 12px
// horizontal overflow and breaks WCAG 1.4.10 Reflow (320px) and 1.4.4
// Resize Text (200%). Clip overflow at the application shell so content
// remains accessible without a horizontal scroll. The per-component
// padding still renders correctly inside the clipped region.
.v-application .v-application--wrap {
	overflow-x: hidden;
}

// Break long unbroken strings (URLs, IDs) so they wrap rather than
// forcing horizontal overflow.
.v-application p,
.v-application li,
.v-application .widget {
	overflow-wrap: anywhere;
	word-break: break-word;
}

// When content-on-hover content is supplied by a Vuetify v-tooltip or
// similar, ensure the trigger remains dismissible with Escape and that
// hovering the tooltip itself does not dismiss it. This is enforced by
// Vuetify internals, but we also annotate stray title attributes so the
// safety net can strip them.
[data-a11y-content-on-hover-dismissible='true'] {
	pointer-events: auto;
}
</style>
